/**
 * TIMEZONE CONSTANTS & HELPERS
 * 
 * Common US timezones for brand selection, plus helpers for conversion.
 * Location: src/constants/timezones.ts
 */

// Common US timezones (what most indie brands will use)
export const US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)', short: 'ET' },
  { value: 'America/Chicago', label: 'Central Time (CT)', short: 'CT' },
  { value: 'America/Denver', label: 'Mountain Time (MT)', short: 'MT' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)', short: 'AZ' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', short: 'PT' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)', short: 'AKT' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)', short: 'HT' },
];

// International timezones
export const INTERNATIONAL_TIMEZONES = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)', short: 'UTC' },
  { value: 'Europe/London', label: 'London (GMT/BST)', short: 'GMT' },
  { value: 'Europe/Paris', label: 'Paris / Berlin (CET)', short: 'CET' },
  { value: 'Europe/Moscow', label: 'Moscow (MSK)', short: 'MSK' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)', short: 'GST' },
  { value: 'Asia/Kolkata', label: 'India (IST)', short: 'IST' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)', short: 'SGT' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)', short: 'JST' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)', short: 'AEST' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST)', short: 'NZST' },
];

// All timezones combined
export const ALL_TIMEZONES = [
  { label: '🇺🇸 United States', options: US_TIMEZONES },
  { label: '🌍 International', options: INTERNATIONAL_TIMEZONES },
];

// Flat list for simple selects
export const TIMEZONE_OPTIONS = [...US_TIMEZONES, ...INTERNATIONAL_TIMEZONES];

/**
 * Get the user's current timezone
 */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Get short label for a timezone (e.g., "PT" for Pacific Time)
 */
export function getTimezoneShort(timezone: string): string {
  const found = TIMEZONE_OPTIONS.find(tz => tz.value === timezone);
  if (found) return found.short;
  
  // Fallback: extract from timezone string
  const parts = timezone.split('/');
  return parts[parts.length - 1].replace('_', ' ');
}

/**
 * Get full label for a timezone
 */
export function getTimezoneLabel(timezone: string): string {
  const found = TIMEZONE_OPTIONS.find(tz => tz.value === timezone);
  if (found) return found.label;
  return timezone;
}

/**
 * Convert a date/time from one timezone to another for display
 * Returns formatted string like "3:00 PM PT (6:00 PM your time)"
 */
export function formatTimeWithConversion(
  date: Date,
  time: string | undefined,
  eventTimezone: string | undefined
): { original: string; local: string; showBoth: boolean } {
  if (!time || !eventTimezone) {
    return { original: '', local: '', showBoth: false };
  }
  
  const userTimezone = getUserTimezone();
  const [hours, minutes] = time.split(':').map(Number);
  
  // Create date in event timezone
  const eventDate = new Date(date);
  eventDate.setHours(hours, minutes, 0, 0);
  
  // Format in event timezone
  const originalTime = eventDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: eventTimezone,
  });
  
  // Format in user's timezone
  const localTime = eventDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: userTimezone,
  });
  
  // Check if timezones are different
  const showBoth = eventTimezone !== userTimezone && originalTime !== localTime;
  
  return {
    original: `${originalTime} ${getTimezoneShort(eventTimezone)}`,
    local: localTime,
    showBoth,
  };
}

/**
 * Convert a wall-clock date + time in a given IANA timezone to the correct
 * absolute UTC Date. `dateStr` = "YYYY-MM-DD", `timeStr` = "HH:MM" (24h).
 *
 * Dependency-free: treats the wall-clock as if it were UTC, then shifts by the
 * zone's actual offset at that instant (read via Intl) so it lands on the true
 * moment. Needed because `new Date(year, month, day)` builds the date in the
 * BROWSER's timezone, not the event's selected one.
 */
export function zonedDateTimeToUtc(dateStr: string, timeStr: string, timeZone: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hour, minute] = timeStr.split(':').map(Number);
  const utcGuess = Date.UTC(year, month - 1, day, hour || 0, minute || 0, 0);
  return new Date(utcGuess - tzOffsetMs(utcGuess, timeZone));
}

/** Milliseconds the timezone is offset from UTC at the given instant (e.g. -6h for MDT). */
function tzOffsetMs(utcMs: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(utcMs));
  const map: Record<string, number> = {};
  for (const p of parts) if (p.type !== 'literal') map[p.type] = Number(p.value);
  const asUtc = Date.UTC(map.year, map.month - 1, map.day, map.hour % 24, map.minute, map.second);
  return asUtc - utcMs;
}

/**
 * Check if a timezone observes DST
 */
export function observesDST(timezone: string): boolean {
  const jan = new Date(2024, 0, 1);
  const jul = new Date(2024, 6, 1);
  
  const janOffset = new Date(jan.toLocaleString('en-US', { timeZone: timezone })).getTimezoneOffset();
  const julOffset = new Date(jul.toLocaleString('en-US', { timeZone: timezone })).getTimezoneOffset();
  
  return janOffset !== julOffset;
}
